// Processor silhouettes on the top face (UX §6.4):
//   Caster: crucible + mould · Smelter: domed furnace with a round glowing mouth · Grinder: hopper + side
//   wheel (the wheel is a moving part) · Press: piston tower (the piston is a moving part) · Loader:
//   three-funnel hopper + cartridge window · Packer: box with inward arrows.
import { LOADER } from '../anchors.ts';
import { fillP, type Part } from '../passes.ts';
import { circle, ellipse, rrect, trapPts } from '../shapes.ts';
import type { MachKit } from './body.ts';

export function casterParts(m: MachKit): Part[] {
  const molten = m.sw('glow.merge');
  const lead = m.sw('material.lead');
  return [
    m.rect('mach.front', 58, 22, 30, 36, 5, {
      detail: (c) => {
        for (const y of [28, 42]) fillP(c, rrect(64, y, 18, 9, 4.5), lead.shade);
      },
    }),
    m.ell('mach.front', 34, 40, 21, 19, {
      detail: (c) => {
        fillP(c, ellipse(34, 36, 14, 10), molten.shade);
        fillP(c, ellipse(34, 35, 11, 7), molten.base);
        fillP(c, ellipse(30, 33, 4, 2.2), molten.hi);
      },
    }),
    m.rect(m.fn, 50, 14, 10, 8, 3),
  ];
}

export function smelterParts(m: MachKit): Part[] {
  const ember = m.sw('tier.5');
  return [
    m.rect('mach.front', 70, 8, 12, 24, 3, {
      detail: (c) => fillP(c, rrect(70, 12, 12, 4, 0), m.pal.hex(m.fn)),
    }),
    m.poly(
      'rock.light',
      [
        [12, 70],
        [12, 42],
        [22, 20],
        [50, 12],
        [78, 20],
        [88, 42],
        [88, 70],
      ],
      14,
      {
        detail: (c) => {
          fillP(c, rrect(12, 44, 76, 5, 0), m.pal.hex(m.fn));
          fillP(c, circle(50, 58, 13), m.sw('mach.outline').base);
          fillP(c, circle(50, 59, 9.5), ember.shade);
          fillP(c, circle(50, 60, 6.5), ember.base);
        },
      },
    ),
  ];
}

export function grinderParts(m: MachKit): Part[] {
  const coal = m.sw('material.coal');
  return [
    m.poly('mach.front', trapPts(40, 12, 58, 26, 50), 5, {
      detail: (c) => {
        fillP(c, ellipse(40, 20, 24, 6), coal.base);
        fillP(c, circle(34, 19, 2.4), m.sw('material.coalGlint').base);
        fillP(c, circle(44, 21, 2), m.sw('material.coalGlint').base);
        fillP(c, rrect(24, 48, 32, 5, 1), m.pal.hex(m.fn));
      },
    }),
    // axle mount for the wheel (mp/grinder/wheel spins over it)
    m.ell('mach.outline', 76, 42, 6, 6),
  ];
}

export function pressParts(m: MachKit): Part[] {
  return [
    m.rect('mach.front', 28, 58, 44, 12, 3),
    m.rect('mach.front', 30, 16, 8, 46, 2),
    m.rect('mach.front', 62, 16, 8, 46, 2),
    m.rect(m.fn, 24, 8, 52, 12, 4),
  ];
}

const FUNNELS = ['material.lead', 'material.casing', 'material.powder'] as const;

export function loaderParts(m: MachKit): Part[] {
  const parts: Part[] = FUNNELS.map((tok, i) => {
    const cx = (LOADER.funnelX[i] ?? 0) * 100;
    return m.poly('mach.front', trapPts(cx, 12, 24, 10, 22), 3, {
      detail: (c) => {
        fillP(c, ellipse(cx, LOADER.fillY * 100, 9.5, 3.2), m.sw(tok).base);
      },
    });
  });
  const glass = m.sw('glow.select');
  parts.push(
    m.rect('mach.front', 16, 40, 68, 26, 5, {
      detail: (c) => {
        fillP(c, rrect(22, 44, 56, 18, 3), glass.shade);
        // a Round lying in the window (casing + lead tip)
        fillP(c, rrect(36, 49, 22, 8, 2), m.sw('material.casing').base);
        fillP(c, ellipse(60, 53, 6, 4), m.sw('material.lead').base);
        fillP(c, rrect(24, 46, 8, 2, 1), glass.hi);
        fillP(c, rrect(18, 40, 64, 3, 0), m.pal.hex(m.fn));
      },
    }),
  );
  return parts;
}

export function packerParts(m: MachKit): Part[] {
  const fn = m.pal.hex(m.fn);
  const arrow = (
    c: Parameters<NonNullable<Part['detail']>>[0],
    x: number,
    y: number,
    rot: number,
  ) => {
    c.save();
    c.translate(x, y);
    c.rotate((rot * Math.PI) / 180);
    c.beginPath();
    c.moveTo(-5, -5);
    c.lineTo(4, 0);
    c.lineTo(-5, 5);
    c.closePath();
    c.fillStyle = fn;
    c.fill();
    c.restore();
  };
  return [
    m.rect('machine.crate', 28, 20, 44, 40, 4, {
      detail: (c) => {
        fillP(c, rrect(28, 36, 44, 6, 0), m.sw('machine.crate').shade);
        fillP(c, rrect(46, 20, 8, 40, 0), m.pal.hex('ui.cream2'));
        arrow(c, 17, 40, 0);
        arrow(c, 83, 40, 180);
        arrow(c, 50, 13, 90);
        arrow(c, 50, 66, -90);
      },
    }),
  ];
}
